import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Test } from '../../types/test';
import Modal from '../../components/Modal';
import './PendingTests.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PendingTests: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingTests, setPendingTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    fetchPendingTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingTests = async () => {
    try {
      const employeeId = user?.id || '';
      const url = employeeId 
        ? `${API_URL}/tests/pending?employee_id=${encodeURIComponent(employeeId)}`
        : `${API_URL}/tests/pending`;
      
      console.log('Fetching pending tests from:', url);
      console.log('Employee ID:', employeeId);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Pending tests received:', data); // Debug log
        setPendingTests(data.tests || []);
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to load tests';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
          console.error('Error response:', errorData);
        } catch (e) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        console.error('Error fetching pending tests:', response.status, errorMessage);
        // Don't show modal for empty results - just show empty state
        if (response.status !== 404) {
          setModal({
            isOpen: true,
            title: 'Error Loading Tests',
            message: `Failed to load tests: ${errorMessage}`,
            type: 'error',
          });
        }
        setPendingTests([]);
      }
    } catch (error) {
      console.error('Error fetching pending tests:', error);
      // Only show modal for network errors, not for empty results
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setModal({
          isOpen: true,
          title: 'Connection Error',
          message: 'Failed to connect to server. Please check if the backend is running.',
          type: 'error',
        });
      }
      setPendingTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTest = (testId: string) => {
    navigate(`/employee/tests/${testId}/take`);
  };

  if (loading) {
    return (
      <div className="pending-tests">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading pending tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-tests">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <div className="pending-tests-header">
        <h1>Pending Tests</h1>
        <p>Tests assigned to you</p>
      </div>

      {pendingTests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h2>No Pending Tests</h2>
          <p>You're all caught up! There are no tests assigned to you at this time.</p>
        </div>
      ) : (
        <div className="tests-grid">
          {pendingTests.map((test) => (
            <div
              key={test.id}
              className="test-card"
              onClick={() => handleSelectTest(test.id)}
            >
              <div className="test-card-header">
                <h3>{test.title}</h3>
                <span className="test-badge">Pending</span>
              </div>
              <p className="test-description">{test.description}</p>
              <div className="test-meta">
                <span>📝 {test.questions.length} Questions</span>
                <span>⏱️ ~{Math.ceil(test.questions.length * 2)} minutes</span>
              </div>
              <button className="test-button">Take Test →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingTests;
